import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useDecisionCategories,
  useDecisionList,
  useDecisionTags,
  useRemoveDecisionVote,
  useToggleDecisionPin,
  useVoteDecision,
} from "../hooks/useDecisions";
import { DecisionCard } from "./DecisionCard";
import {
  NewDecisionDrawer,
  type DecisionPrefill,
} from "./NewDecisionDrawer";
import type { DecisionListItem } from "../types";

type Filter = {
  companyId?: string;
  productId?: string;
  projectId?: string;
  meetingId?: string;
};

type Props = {
  filter: Filter;
  prefill: DecisionPrefill;
};

export function DecisionsTab({ filter, prefill }: Props) {
  const navigate = useNavigate();
  const [newOpen, setNewOpen] = useState(false);

  const enabled = Boolean(
    filter.companyId ||
      filter.productId ||
      filter.projectId ||
      filter.meetingId,
  );

  const { data, isLoading } = useDecisionList(
    enabled
      ? {
          page: 1,
          limit: 50,
          ...filter,
        }
      : { page: 1, limit: 0 },
  );

  const togglePin = useToggleDecisionPin();
  const categories = useDecisionCategories().data ?? [];
  const tagOptions = useDecisionTags().data ?? [];

  const tagLabels = useMemo(
    () => Object.fromEntries(tagOptions.map((t) => [t.value, t.label])),
    [tagOptions],
  );
  const categoryLabels = useMemo(
    () => Object.fromEntries(categories.map((t) => [t.value, t.label])),
    [categories],
  );

  const decisions: DecisionListItem[] = enabled ? data?.data ?? [] : [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {decisions.length} decision{decisions.length === 1 ? "" : "s"}
        </div>
        <Button size="sm" onClick={() => setNewOpen(true)}>
          <Plus className="h-4 w-4" strokeWidth={1.5} />
          New Decision
        </Button>
      </div>

      {isLoading && enabled ? (
        <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
          Loading decisions…
        </div>
      ) : decisions.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          No decisions linked yet
        </div>
      ) : (
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}
        >
          {decisions.map((d) => (
            <DecisionCardWithVote
              key={d.id}
              decision={d}
              onClick={() => navigate(`/decisions/${d.id}`)}
              onTogglePin={() =>
                togglePin.mutate({ id: d.id, isPinned: !d.isPinned })
              }
              tagLabels={tagLabels}
              categoryLabels={categoryLabels}
            />
          ))}
        </div>
      )}

      <NewDecisionDrawer
        isOpen={newOpen}
        onClose={() => setNewOpen(false)}
        prefill={prefill}
      />
    </div>
  );
}

function DecisionCardWithVote(props: {
  decision: DecisionListItem;
  onClick: () => void;
  onTogglePin: () => void;
  tagLabels: Record<string, string>;
  categoryLabels: Record<string, string>;
}) {
  const vote = useVoteDecision(props.decision.id);
  const removeVote = useRemoveDecisionVote(props.decision.id);

  async function handle(type: "up" | "down") {
    try {
      if (props.decision.currentUserVote === type) {
        await removeVote.mutateAsync();
      } else {
        await vote.mutateAsync(type);
      }
    } catch {
      // swallow
    }
  }

  return (
    <DecisionCard
      decision={props.decision}
      onClick={props.onClick}
      onTogglePin={props.onTogglePin}
      onVote={handle}
      tagLabels={props.tagLabels}
      categoryLabels={props.categoryLabels}
    />
  );
}
